<template>
  <View direction="column" gap="lg">
    <View direction="column" gap="xs">
      <Text variant="h2">Security</Text>
      <Text variant="body-large" color="secondary">Password, two-factor authentication and signed-in devices</Text>
    </View>

    <View direction="column" gap="md">
      <!-- Every section title carries a secondary description (like 2FA and the channel picker);
           without it the bold title stacks straight onto the first field label as a double header -->
      <View direction="column" gap="xs">
        <Text variant="h5">Change password</Text>
        <Text variant="body-medium" color="secondary">Changing it signs your other devices out</Text>
      </View>

      <FormField label="Current password" :error="form.fieldErrors('currentPassword')">
        <PasswordInput v-model="form.fields.currentPassword" autocomplete="current-password" />
      </FormField>

      <div class="password-grid">
        <FormField label="New password" :error="form.fieldErrors('newPassword')">
          <PasswordInput
            v-model="form.fields.newPassword"
            placeholder="Your new password"
            autocomplete="new-password"
            @focus="showPasswordStrength = true"
          />
        </FormField>
        <FormField label="Confirm new password" :error="form.fieldErrors('confirmPassword')">
          <PasswordInput
            v-model="form.fields.confirmPassword"
            placeholder="Repeat new password"
            autocomplete="new-password"
          />
        </FormField>
      </div>

      <PasswordStrength :password="form.fields.newPassword" :show-component="showPasswordStrength" />

      <View>
        <Button :loading="form.submitting" @click="updatePassword">Update password</Button>
      </View>
    </View>

    <Divider />

    <View align="center" gap="md">
      <View direction="column" gap="xs" :grow="1" class="two-factor-copy">
        <Text variant="h5">Two-factor authentication</Text>
        <Text variant="body-medium" color="secondary">Require a one-time code from your phone when signing in</Text>
      </View>
      <!-- Click is intercepted before the native checkbox so its DOM state can't drift from
           the store; the switch flips only once the modal flow really enables/disables 2FA -->
      <div class="two-factor-switch" @click.capture.stop.prevent="onTwoFactorToggle">
        <Switch :model-value="isTwoFactorEnabled" />
      </div>
    </View>

    <Divider />

    <View direction="column" gap="md">
      <View direction="column" gap="xs">
        <Text variant="h5">Active sessions</Text>
        <Text variant="body-medium" color="secondary">Devices where your account is signed in</Text>
      </View>

      <View v-if="isLoadingSessions" justify="center" padding="md">
        <LoadingIndicator />
      </View>

      <View v-else-if="sessions.length" direction="column" gap="sm">
        <div v-for="session in sessions" :key="session.id" class="session-row">
          <View direction="column" gap="xs" :grow="1" class="session-info">
            <Text variant="label-medium" as="div">
              {{ session.deviceName || 'Unknown device' }}
              <span v-if="session.isCurrent" class="current-marker">— this device</span>
            </Text>
            <Text variant="body-medium" color="tertiary">
              {{ sessionMeta(session) }}
            </Text>
          </View>
          <Button v-if="!session.isCurrent" variant="ghost" size="small" @click="signOutModalRef?.show(session)">
            Sign out
          </Button>
        </div>
      </View>

      <Hint v-else icon="info-circle">
        Only sessions started after this update show up here. Sign in again on a device to see it listed.
      </Hint>
    </View>
  </View>

  <TwoFactorSetupModal ref="twoFactorSetupModalRef" @enabled="loadSessions" />
  <TwoFactorDisableModal ref="twoFactorDisableModalRef" />
  <SessionSignOutModal ref="signOutModalRef" @revoked="loadSessions" />
</template>

<script setup lang="ts">
import { helpers, required, sameAs } from '@vuelidate/validators';
import { storeToRefs } from 'pinia';
import { computed, onMounted, reactive, ref } from 'vue';

import {
  Button,
  Divider,
  extractErrorMessage,
  FormField,
  Hint,
  LoadingIndicator,
  PasswordInput,
  passwordMeetsAllRequirements,
  PasswordStrength,
  Switch,
  Text,
  useAlertsStore,
  useForm,
  View,
} from '@tallo/design-system';
import { profileService, SessionDto, useInvestorStore } from '@tallo/investor/investor';

import SessionSignOutModal from '../modals/session-sign-out-modal.vue';
import TwoFactorDisableModal from '../modals/two-factor-disable-modal.vue';
import TwoFactorSetupModal from '../modals/two-factor-setup-modal.vue';

const investorStore = useInvestorStore();
const alertsStore = useAlertsStore();
const { investor } = storeToRefs(investorStore);

const user = computed(() => investor.value?.user);
const isTwoFactorEnabled = computed(() => Boolean(user.value?.twoFactorEnabledAt));

const twoFactorSetupModalRef = ref<InstanceType<typeof TwoFactorSetupModal>>();
const twoFactorDisableModalRef = ref<InstanceType<typeof TwoFactorDisableModal>>();
const signOutModalRef = ref<InstanceType<typeof SessionSignOutModal>>();

const sessions = ref<SessionDto[]>([]);
const isLoadingSessions = ref(false);

const passwordFields = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
});

const showPasswordStrength = ref(false);

const form = useForm(passwordFields, {
  newPassword: {
    required: helpers.withMessage('New password is required', required),
    strong: helpers.withMessage('Password does not meet the requirements below', (value: string) =>
      passwordMeetsAllRequirements(value),
    ),
  },
  confirmPassword: {
    sameAs: helpers.withMessage('Passwords do not match', sameAs(computed(() => passwordFields.newPassword))),
  },
});

const isDirty = computed(() =>
  Boolean(form.fields.currentPassword || form.fields.newPassword || form.fields.confirmPassword),
);

function reset() {
  form.fields.currentPassword = '';
  form.fields.newPassword = '';
  form.fields.confirmPassword = '';
  showPasswordStrength.value = false;
  form.resetValidation();
}

function updatePassword() {
  form.submit(async () => {
    try {
      await profileService.changePassword(form.fields.newPassword, form.fields.currentPassword || undefined);
      alertsStore.success('Password updated');
      reset();
      // The backend signs out every other device on a password change
      loadSessions();
    } catch (error) {
      form.setFieldError('currentPassword', extractErrorMessage(error, 'Could not update the password'));
    }
  });
}

function onTwoFactorToggle() {
  if (isTwoFactorEnabled.value) {
    twoFactorDisableModalRef.value?.show();
  } else {
    twoFactorSetupModalRef.value?.show();
  }
}

async function loadSessions() {
  isLoadingSessions.value = true;

  try {
    sessions.value = await profileService.listSessions();
  } catch (error) {
    alertsStore.error(extractErrorMessage(error, 'Could not load active sessions'));
  } finally {
    isLoadingSessions.value = false;
  }
}

function sessionMeta(session: SessionDto): string {
  const parts = [session.ip ? `IP ${session.ip}` : null, formatLastSeen(session)].filter(Boolean);
  return parts.join(' · ');
}

function formatLastSeen(session: SessionDto): string {
  if (session.isCurrent) return 'Active now';

  const minutes = Math.floor((Date.now() - new Date(session.lastSeenAt).getTime()) / 60000);
  if (minutes < 5) return 'Active now';
  if (minutes < 60) return `${minutes} minutes ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;

  const days = Math.floor(hours / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

onMounted(loadSessions);

defineExpose({ isDirty, reset });
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.password-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--t-spacing-md);

  // Matches the field grids: 2-col only in the roomy sidebar layout, 1-col whenever narrow/collapsed
  @include screen-min-lg {
    grid-template-columns: 1fr 1fr;
  }
}

.two-factor-copy {
  min-width: 0;
}

.session-row {
  display: flex;
  align-items: center;
  gap: var(--t-spacing-md);
  padding: var(--t-spacing-md);
  border-radius: var(--t-border-radius-md);
  background: var(--t-color-neutral-50);
}

.session-info {
  min-width: 0;
}

.current-marker {
  font: var(--t-font-weight-medium) 0.75rem/1rem var(--t-font-family-primary);
  color: var(--t-color-green-500);
}
</style>
